/**
 * The fighter state vocabulary. ARCHITECTURE.md §7.1.
 *
 * Exactly one state is active at a time. Derived properties (airborne, guarding, …) are
 * never stored — they are computed from the state plus a few flags, so they can never drift
 * out of sync with the state itself.
 *
 * Three-free: runs in Node for headless combat tests.
 */

export const S = Object.freeze({
  IDLE: 'idle',
  WALK_FWD: 'walk-fwd',
  WALK_BACK: 'walk-back',
  DASH_FWD: 'dash-fwd',
  DASH_BACK: 'dash-back',
  BACKDASH_RECOVERY: 'backdash-recovery',
  SIDESTEP_LEFT: 'sidestep-left',
  SIDESTEP_RIGHT: 'sidestep-right',
  SIDESTEP_RECOVERY: 'sidestep-recovery',

  CROUCH_ENTER: 'crouch-enter',
  CROUCH: 'crouch',
  CROUCH_EXIT: 'crouch-exit',

  GUARD_STAND: 'guard-stand',
  GUARD_CROUCH: 'guard-crouch',
  GUARD_RECOVERY: 'guard-recovery',
  PARRY_ACTIVE: 'parry-active',
  PARRY_SUCCESS: 'parry-success',

  ATTACK_STARTUP: 'attack-startup',
  ATTACK_ACTIVE: 'attack-active',
  ATTACK_RECOVERY: 'attack-recovery',

  THROW_ATTEMPT: 'throw-attempt',
  THROW_HOLD: 'throw-hold',
  THROW_EXECUTE: 'throw-execute',
  THROW_BROKEN: 'throw-broken',
  THROWN: 'thrown',
  THROW_TECH: 'throw-tech',

  HITSTUN_STAND: 'hitstun-stand',
  HITSTUN_CROUCH: 'hitstun-crouch',
  HITSTUN_LAUNCH: 'hitstun-launch',
  HITSTUN_AIR: 'hitstun-air',
  HITSTUN_WALL: 'hitstun-wall',

  WALL_SPLAT: 'wall-splat',
  WALL_SLUMP: 'wall-slump',

  KNOCKDOWN_FALL: 'knockdown-fall',
  KNOCKDOWN_GROUND: 'knockdown-ground',
  WAKEUP_NEUTRAL: 'wakeup-neutral',
  WAKEUP_ATTACK: 'wakeup-attack',
  WAKEUP_ROLL: 'wakeup-roll',

  AIRBORNE: 'airborne',
  AIR_ATTACK: 'air-attack',
  LANDING: 'landing',
  HARD_LANDING: 'hard-landing',

  STANCE_SHIFT: 'stance-shift',
  SPECIAL_CHARGE: 'special-charge',
  FINISHER_ATTACKER: 'finisher-attacker',
  FINISHER_VICTIM: 'finisher-victim',

  ROUND_INTRO: 'round-intro',
  ROUND_WIN: 'round-win',
  ROUND_LOSS: 'round-loss',
  MATCH_WIN: 'match-win',
  MATCH_LOSS: 'match-loss',
});

export const ALL_STATES = Object.freeze(Object.values(S));

// ---------------------------------------------------------------------------
// State classification sets. Membership drives every derived predicate below,
// so adding a state forces an explicit decision about how it behaves.
// ---------------------------------------------------------------------------

const AIRBORNE_STATES = new Set([
  S.AIRBORNE, S.AIR_ATTACK, S.HITSTUN_LAUNCH, S.HITSTUN_AIR, S.KNOCKDOWN_FALL,
]);

const CROUCHING_STATES = new Set([
  S.CROUCH, S.CROUCH_ENTER, S.CROUCH_EXIT, S.GUARD_CROUCH, S.HITSTUN_CROUCH,
]);

const GUARDING_STATES = new Set([S.GUARD_STAND, S.GUARD_CROUCH]);

const ATTACKING_STATES = new Set([
  S.ATTACK_STARTUP, S.ATTACK_ACTIVE, S.ATTACK_RECOVERY, S.AIR_ATTACK,
  S.THROW_ATTEMPT, S.THROW_HOLD, S.THROW_EXECUTE, S.WAKEUP_ATTACK,
  S.FINISHER_ATTACKER, S.SPECIAL_CHARGE,
]);

/** States in which the fighter cannot act and is taking a hit. */
const HITSTUN_STATES = new Set([
  S.HITSTUN_STAND, S.HITSTUN_CROUCH, S.HITSTUN_LAUNCH, S.HITSTUN_AIR, S.HITSTUN_WALL,
  S.WALL_SPLAT, S.WALL_SLUMP, S.THROWN, S.FINISHER_VICTIM,
]);

/** States where the fighter is on the floor and can only be hit by grounded-hitting moves. */
const GROUNDED_DOWN_STATES = new Set([S.KNOCKDOWN_GROUND]);

/** States that cannot be thrown. */
const UNTHROWABLE_STATES = new Set([
  S.AIRBORNE, S.AIR_ATTACK, S.HITSTUN_LAUNCH, S.HITSTUN_AIR, S.KNOCKDOWN_FALL,
  S.KNOCKDOWN_GROUND, S.THROWN, S.THROW_TECH, S.WALL_SPLAT, S.WALL_SLUMP,
  S.FINISHER_VICTIM, S.ROUND_INTRO, S.ROUND_WIN, S.ROUND_LOSS, S.MATCH_WIN, S.MATCH_LOSS,
  S.HITSTUN_STAND, S.HITSTUN_CROUCH,
]);

/** States in which the fighter is recovering and therefore punishable. */
const RECOVERING_STATES = new Set([
  S.ATTACK_RECOVERY, S.BACKDASH_RECOVERY, S.SIDESTEP_RECOVERY, S.GUARD_RECOVERY,
  S.THROW_BROKEN, S.LANDING, S.HARD_LANDING, S.CROUCH_EXIT,
]);

/** States that ignore player input entirely. */
const LOCKED_STATES = new Set([
  ...HITSTUN_STATES, S.THROW_EXECUTE, S.THROW_BROKEN, S.KNOCKDOWN_FALL,
  S.HARD_LANDING, S.FINISHER_ATTACKER, S.ROUND_INTRO, S.ROUND_WIN, S.ROUND_LOSS,
  S.MATCH_WIN, S.MATCH_LOSS, S.PARRY_SUCCESS,
]);

/** States from which a neutral action can begin immediately. */
const ACTIONABLE_STATES = new Set([
  S.IDLE, S.WALK_FWD, S.WALK_BACK, S.CROUCH, S.GUARD_STAND, S.GUARD_CROUCH,
  S.DASH_FWD, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT, S.STANCE_SHIFT,
]);

/** Juggleable: an airborne hitstun state that combos can extend. */
const JUGGLEABLE_STATES = new Set([S.HITSTUN_LAUNCH, S.HITSTUN_AIR, S.KNOCKDOWN_FALL]);

// ---------------------------------------------------------------------------
// Derived predicates
// ---------------------------------------------------------------------------

export const isAirborneState = (s) => AIRBORNE_STATES.has(s);
export const isCrouchingState = (s) => CROUCHING_STATES.has(s);
export const isGuardingState = (s) => GUARDING_STATES.has(s);
export const isAttackingState = (s) => ATTACKING_STATES.has(s);
export const isHitstunState = (s) => HITSTUN_STATES.has(s);
export const isDownState = (s) => GROUNDED_DOWN_STATES.has(s);
export const isThrowableState = (s) => !UNTHROWABLE_STATES.has(s);
export const isRecoveringState = (s) => RECOVERING_STATES.has(s);
export const isLockedState = (s) => LOCKED_STATES.has(s);
export const isActionableState = (s) => ACTIONABLE_STATES.has(s);
export const isJuggleableState = (s) => JUGGLEABLE_STATES.has(s);
export const isGroundedState = (s) => !AIRBORNE_STATES.has(s);

/** Rounds/intros/results — the simulation is not accepting combat input. */
export const isNonCombatState = (s) =>
  s === S.ROUND_INTRO || s === S.ROUND_WIN || s === S.ROUND_LOSS || s === S.MATCH_WIN || s === S.MATCH_LOSS;

/**
 * Counter-hit window: a fighter struck during startup or during a committed
 * movement/recovery frame eats a counter-hit. This is the whole reason a whiff
 * punish feels different from a poke trade.
 */
export const isCounterHitState = (s) =>
  s === S.ATTACK_STARTUP ||
  s === S.ATTACK_ACTIVE ||
  s === S.THROW_ATTEMPT ||
  s === S.SPECIAL_CHARGE ||
  s === S.STANCE_SHIFT ||
  s === S.DASH_FWD;

/** Punish window: hitting a fighter in recovery is a punish, worth bonus scaling. */
export const isPunishableState = (s) => RECOVERING_STATES.has(s);

/**
 * Category label used by animation and the dev overlay to pick a broad pose family.
 * @returns {'neutral'|'move'|'crouch'|'guard'|'attack'|'throw'|'hurt'|'down'|'air'|'special'|'ceremony'}
 */
export function stateCategory(s) {
  if (isNonCombatState(s)) return 'ceremony';
  if (isHitstunState(s)) return 'hurt';
  if (isDownState(s) || s === S.KNOCKDOWN_FALL || s.startsWith('wakeup')) return 'down';
  if (s === S.THROW_ATTEMPT || s === S.THROW_HOLD || s === S.THROW_EXECUTE || s === S.THROW_BROKEN
      || s === S.THROW_TECH) return 'throw';
  if (isAirborneState(s)) return 'air';
  if (isAttackingState(s)) return 'attack';
  if (isGuardingState(s) || s === S.GUARD_RECOVERY || s === S.PARRY_ACTIVE || s === S.PARRY_SUCCESS) return 'guard';
  if (isCrouchingState(s)) return 'crouch';
  if (s === S.STANCE_SHIFT || s === S.SPECIAL_CHARGE || s === S.FINISHER_ATTACKER) return 'special';
  if (s === S.IDLE) return 'neutral';
  return 'move';
}
